import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Pipe, APP_INITIALIZER, NgModule } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
const CL_TRANSLATIONS = new InjectionToken('CL_TRANSLATIONS');
let CustomLocaleService = /*#__PURE__*/(() => {
  class CustomLocaleService {
    translations;
    translationCache = new Map();
    ready$ = new BehaviorSubject(false);
    language = 'en';
    /*
     */
    constructor(translations) {
      this.translations = translations;
      this.setTranslations(translations);
    }
    /*
     */
    isReady() {
      return this.ready$.value;
    }
    isReady$() {
      return this.ready$.asObservable();
    }
    /*
     */
    setTranslations(translations) {
      this.translations = translations || {};
      this.translationCache.clear();
      this.ready$.next(true);
    }
    /*
     */
    getTranslations() {
      return this.translations;
    }
    /*
     */
    translate(key, options = {}) {
      if (typeof key !== 'string' || !key.trim()) {
        return '';
      }
      let template;
      if (this.translationCache.has(key)) {
        template = this.translationCache.get(key);
      } else {
        const value = this.deepGet(this.translations, key);
        template = typeof value === 'string' ? value : undefined;
        this.translationCache.set(key, template);
      }
      if (!template) {
        return key;
      }
      if (!/[{}%:]/.test(template)) {
        return template;
      }
      return template.replace(/{{\s*(\w[\w\.]*)\s*}}|{\s*(\w[\w\.]*)\s*}|%\w+%|:\w+/g, match => {
        let token = match;
        if (match.startsWith('{{')) {
          token = match.replace(/[{}]/g, '').trim();
        } else if (match.startsWith('{') && match.endsWith('}')) {
          token = match.replace(/[{}]/g, '').trim();
        } else if (match.startsWith('%') && match.endsWith('%')) {
          token = match.slice(1, -1);
        } else if (match.startsWith(':')) {
          token = match.slice(1);
        }
        const value = this.deepGet(options, token);
        return value != null ? String(value) : '';
      });
    }
    /*
     */
    deepGet(obj, path) {
      if (typeof path !== 'string') return undefined;
      return path.split('.').reduce((acc, part) => {
        return acc && acc[part] !== undefined ? acc[part] : undefined;
      }, obj);
    }
    /*
     */
    instant(key, options = {}) {
      return this.removeZeroWidthSpace(this.translate(key, options));
    }
    /*
     */
    directInstant(key) {
      return this.removeZeroWidthSpace(this.translate(key));
    }
    /*
     */
    use(lang) {
      this.language = lang;
    }
    /*
     */
    get currentLang() {
      return this.language;
    }
    /*
     */
    hasZeroWidthSpace(str) {
      // Zero width space (U+200B)
      // Zero width non-joiner (U+200C)
      // Zero width joiner (U+200D)
      // Zero width no-break space (U+FEFF)
      return /[\u200B\u200C\u200D\uFEFF]/.test(str);
    }
    /*
     */
    removeZeroWidthSpace(str) {
      // Удаляем все zero width символы:
      // Zero width space (U+200B)
      // Zero width non-joiner (U+200C)
      // Zero width joiner (U+200D)
      // Zero width no-break space (U+FEFF)
      return str.replace(/[\u200B\u200C\u200D\uFEFF]/g, '');
    }
    static ɵfac = function CustomLocaleService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || CustomLocaleService)(i0.ɵɵinject(CL_TRANSLATIONS));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: CustomLocaleService,
      factory: CustomLocaleService.ɵfac,
      providedIn: 'root'
    });
  }
  return CustomLocaleService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CustomLocale = /*#__PURE__*/(() => {
  class CustomLocale {
    localeService;
    constructor(localeService) {
      this.localeService = localeService;
    }
    transform(k, opts = {}) {
      return this.localeService.translate(k, opts);
    }
    static ɵfac = function CustomLocale_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || CustomLocale)(i0.ɵɵdirectiveInject(CustomLocaleService, 16));
    };
    static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "customLocale",
      type: CustomLocale,
      pure: true
    });
  }
  return CustomLocale;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
*/
const GLOBAL_KEY = '__custom__locale__translations';
function initTranslationsFactory(inputTranslations) {
  return () => {
    const win = window;
    return new Promise(resolve => {
      if (inputTranslations && Object.keys(inputTranslations).length > 0) {
        win[GLOBAL_KEY] = inputTranslations;
        return resolve();
      }
      if (win[GLOBAL_KEY]) {
        return resolve();
      }
      const onMessage = e => {
        if (e.data?.type === 'translations') {
          win[GLOBAL_KEY] = e.data.payload || {};
          window.removeEventListener('message', onMessage);
          resolve();
        }
      };
      window.addEventListener('message', onMessage);
      try {
        window.parent.postMessage({
          type: 'get_translations'
        }, '*');
      } catch {
        resolve(); // fallback
      }
      // safety fallback 5 sec
      setTimeout(() => {
        win[GLOBAL_KEY] = {};
        window.removeEventListener('message', onMessage);
        resolve();
      }, 5000);
    });
  };
}
/*
*/
function getCachedTranslations() {
  const win = window;
  return win[GLOBAL_KEY] || {};
}
/*
*/
let CustomLocaleModule = /*#__PURE__*/(() => {
  class CustomLocaleModule {
    static forRoot(translations) {
      return {
        ngModule: CustomLocaleModule,
        providers: [{
          provide: APP_INITIALIZER,
          useFactory: () => initTranslationsFactory(translations),
          multi: true
        }, {
          provide: CL_TRANSLATIONS,
          useFactory: getCachedTranslations
        }]
      };
    }
    static ɵfac = function CustomLocaleModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || CustomLocaleModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: CustomLocaleModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return CustomLocaleModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CL_TRANSLATIONS, CustomLocale, CustomLocaleModule, CustomLocaleService, getCachedTranslations };
